// English content (default language)

export const ui = {
  logo: 'QUBO Car Tech',
  nav: [
    { label: 'Technology', href: '#tecnologia' },
    { label: 'Products', href: '#productos' },
    { label: 'Sectors', href: '#sectores' },
    { label: 'Model', href: '#modelo' }
  ],
  contact: 'Contact',
  languageNames: { en: 'English', es: 'Español' },
  hero: {
    imageAlt: 'Portal Qubo - Vehicle inspection system',
    stats: [
      { value: '<60s', label: 'Full inspection' },
      { value: '360°', label: 'Visual coverage' },
      { value: 'High', label: 'AI accuracy' },
      { value: '24/7', label: 'Operation' }
    ]
  },
  valueProposition: {
    title: 'Value proposition',
    subtitle: 'We turn vehicle inspection into an objective, fast and scalable process'
  },
  sectors: {
    title: 'Sectors',
    subtitle: 'Solutions designed to maximize return on investment in every sector',
    benefitsLabel: (count) => `${count} benefits`,
    highlightLabel: 'Proven ROI:',
    highlightText: 'Every sector gets measurable benefits from day one of operation'
  },
  technology: {
    imageAlt: 'Portal Qubo - Inspection technology',
    detectionTitle: 'What do we detect?',
    detectionSubtitle: 'Our artificial intelligence system automatically identifies more than 20 different elements of the vehicle',
    detectionFooter: '+ More capabilities being added continuously with AI updates',
    metrics: [
      { value: '4K', label: 'Camera resolution' },
      { value: '12+', label: 'Capture points' },
      { value: 'AI', label: 'Computer vision' },
      { value: 'EDGE', label: 'Proprietary architecture with real-time processing' }
    ]
  },
  products: {
    title: 'Products',
    subtitle: 'A comprehensive technology solution combining hardware, software and artificial intelligence',
    bottomLabel: 'Modular solution:',
    bottomText: 'Get the components you need and scale according to your operation'
  },
  scale: {
    imageAlt: 'Industrial installation of multiple Qubo portals',
    badgeTitle: 'Multi-Portal Installation',
    badgeSubtitle: 'Live in production'
  },
  howItWorks: {
    title: 'How does it work?',
    subtitle: 'A simple, automated four-step process',
    flow: [
      { title: 'Vehicle', subtitle: 'Entry' },
      { title: 'Capture', subtitle: '360° images' },
      { title: 'AI Analysis', subtitle: 'Processing' },
      { title: 'Data', subtitle: 'Reports & APIs' }
    ]
  },
  competitiveAdvantages: {
    title: 'Competitive advantages',
    subtitle: 'Technology designed to scale, innovate and lead the market from day one',
    bottomTitle: 'Technology ready to grow globally',
    bottomText: 'Modular architecture, robust design and integration capabilities that allow expansion into new markets without re-engineering'
  },
  whyQubo: {
    beforeLabel: 'Before',
    afterLabel: 'With Qubo',
    bottomTitle: 'Operational transformation, not just technology',
    bottomText: 'Qubo doesn’t replace a manual process with a digital one. It creates a complete data ecosystem that improves every decision in your vehicle operation.'
  },
  faq: {
    title: 'Frequently asked questions',
    subtitle: 'Everything you need to know about Qubo',
    contactTitle: 'Have more questions?',
    contactText: 'Our team is ready to help you find the perfect solution for your operation',
    contactCta: 'Contact us →'
  },
  contactForm: {
    title: 'Get in touch',
    subtitle: 'Tell us about your operation and we’ll help you find the right fit',
    nameLabel: 'Name',
    namePlaceholder: 'Your name',
    emailLabel: 'Email',
    emailPlaceholder: 'you@company.com',
    messageLabel: 'Message',
    messagePlaceholder: 'Tell us about your fleet, dealership or operation...',
    submit: 'Send message',
    submitting: 'Sending...',
    successMessage: 'Message sent! We’ll get back to you soon.',
    errorMessage: 'Something went wrong. Please try again or email us directly.',
    emailSubject: 'New contact form submission - Qubo Car Tech'
  },
  footer: {
    ctaTitle: 'Transform your vehicle operation',
    ctaSubtitle: 'Join the companies revolutionizing vehicle inspection with cutting-edge technology',
    ctaButton: 'Explore technology',
    description: 'Intelligent infrastructure for vehicle inspection. Turning every inspection into structured, actionable data.',
    productsHeading: 'Products',
    productLinks: ['Portal Qubo', 'Qubo Platform', 'Integrations'],
    companyHeading: 'Company',
    companyLinks: ['About us', 'Contact', 'Blog'],
    rightsReserved: (year) => `© ${year} Qubo Car Tech. All rights reserved.`,
    legalLinks: ['Privacy', 'Terms', 'Cookies']
  }
};

export const hero = {
  title: 'Intelligent infrastructure for vehicle inspection',
  subtitle: 'Qubo turns every inspection into structured, auditable and actionable data for businesses.',
  ctaPrimary: 'Explore technology',
  ctaSecondary: null,
  heroImage: 'https://customer-assets.emergentagent.com/job_auto-scan-ai/artifacts/248osucc_33c92793-73f5-4e63-bfca-f5c6aa2480d6.png'
};

export const valuePropositions = [
  {
    id: 1,
    title: 'Inspections in seconds',
    description: 'Complete vehicle capture in under 60 seconds, optimizing operational time.',
    icon: 'zap'
  },
  {
    id: 2,
    title: 'Objective AI detection',
    description: 'Automated analysis that eliminates human error and guarantees consistent results.',
    icon: 'cpu'
  },
  {
    id: 3,
    title: 'Structured history',
    description: 'Every inspection creates a permanent, auditable record of the vehicle’s condition.',
    icon: 'database'
  },
  {
    id: 4,
    title: 'Operational improvement',
    description: 'Actionable data that lets you optimize processes and make better business decisions.',
    icon: 'trending-up'
  }
];

export const sectors = [
  {
    id: 1,
    title: 'Fleets',
    icon: 'truck',
    benefits: [
      {
        title: 'Lower costs and higher margin per vehicle',
        description: 'We detect damage early and prevent major repairs.'
      },
      {
        title: 'Predictive maintenance that reduces downtime',
        description: 'We anticipate failures before they affect operations.'
      },
      {
        title: 'Total real-time fleet visibility',
        description: 'Centralized control over the condition of every vehicle.'
      }
    ]
  },
  {
    id: 2,
    title: 'Automotive dealerships',
    icon: 'store',
    benefits: [
      {
        title: 'More bodywork and paint revenue',
        description: 'Automatic damage detection that generates immediate sales.'
      },
      {
        title: 'Increased service shop revenue',
        description: 'Accurate identification of necessary maintenance and repairs.'
      },
      {
        title: 'Higher average ticket per customer',
        description: 'Every inspection becomes an additional revenue opportunity.'
      },
      {
        title: 'Automatic logging of vehicle check-ins and check-outs',
        description: 'Full real-time inventory control, with no manual processes.'
      },
      {
        title: 'Higher conversion through total transparency',
        description: 'Customers accept more services when they see objective evidence.'
      },
      {
        title: 'Increased value on pre-owned vehicles',
        description: 'Verifiable history that boosts buyer confidence and resale price.'
      }
    ]
  },
  {
    id: 3,
    title: 'Insurance companies',
    icon: 'shield-check',
    benefits: [
      {
        title: 'Direct reduction in fraud',
        description: 'Comparison against the vehicle’s prior history.'
      },
      {
        title: 'Lower claims-handling costs',
        description: 'Faster, more objective processes.'
      },
      {
        title: 'Better risk assessment based on real data',
        description: 'Accurate information for more sound decisions.'
      }
    ]
  },
  {
    id: 4,
    title: 'Rental companies',
    icon: 'key',
    benefits: [
      {
        title: 'Fewer disputes and unreported damages',
        description: 'Objective evidence at every pickup and return.'
      },
      {
        title: 'Higher profitability per vehicle',
        description: 'Fewer losses and better operational control.'
      }
    ]
  },
  {
    id: 5,
    title: 'Airports',
    icon: 'plane',
    benefits: [
      {
        title: 'Automatic inspection at rental check-in and check-out',
        description: 'Instant condition record at every pickup and return.'
      },
      {
        title: 'Fewer customer disputes in high-traffic areas',
        description: 'Objective evidence that eliminates conflicts in rental operations.'
      },
      {
        title: 'Faster vehicle flow',
        description: 'Inspections in seconds that avoid bottlenecks during peak hours.'
      },
      {
        title: 'Operational optimization for rental companies',
        description: 'More efficient processes in a high-volume, high-demand environment.'
      },
      {
        title: 'Infrastructure ready for 24/7 operations',
        description: 'Scalable to thousands of vehicles per day without friction.'
      }
    ]
  },
  {
    id: 6,
    title: 'Public sector',
    icon: 'building',
    benefits: [
      {
        title: 'Total transparency in vehicle use',
        description: 'Verifiable record of each unit’s condition and use.'
      },
      {
        title: 'Reduced misuse and corruption',
        description: 'Objective evidence that limits abuse and misuse.'
      },
      {
        title: 'Optimized public maintenance spending',
        description: 'Early detection that avoids higher costs.'
      },
      {
        title: 'Centralized fleet control',
        description: 'Real-time oversight from a single platform.'
      },
      {
        title: 'Complete traceability for audits',
        description: 'Accessible history that facilitates reviews and compliance.'
      }
    ]
  }
];

export const technologyData = {
  title: 'From inspection to intelligence',
  description: 'Qubo captures, analyzes and structures vehicle information to turn it into strategic decisions.',
  steps: [
    {
      id: 1,
      title: 'Capture',
      description: 'The vehicle passes through the portal while high-resolution cameras capture images from every angle.'
    },
    {
      id: 2,
      title: 'Analysis',
      description: 'Artificial intelligence processes the images and automatically identifies damage, components and overall condition.'
    },
    {
      id: 3,
      title: 'Structuring',
      description: 'Findings are turned into structured data, classified and located on the vehicle.'
    },
    {
      id: 4,
      title: 'Action',
      description: 'Data is integrated into operational systems, generating reports, alerts and predictive analytics.'
    }
  ],
  detectionCapabilities: [
    'Oxidation and corrosion',
    'Scratches and scrapes',
    'Dents and deformations',
    'Structural impacts',
    'Paint stains',
    'Dirt and contaminants',
    'Body alignment',
    'Tire pressure',
    'Tire wear',
    'Windshield cracks',
    'Glass condition',
    'Fluid leaks',
    'Headlight and light condition',
    'Trim condition',
    'Mirror damage',
    'License plate reading (OCR)',
    'VIN number',
    'Tire brand and model',
    'Fuel caps'
  ],
  image: '/scanner.jpg'
};

export const products = [
  {
    id: 1,
    title: 'Portal Qubo',
    description: 'Physical vehicle inspection system with next-generation hardware, high-resolution cameras and edge processing.',
    features: [
      'Modular installation',
      '360° capture in seconds',
      'Autonomous operation',
      'Minimal maintenance'
    ],
    image: '/scanner.jpg',
    badge: 'Hardware',
    icon: 'box'
  },
  {
    id: 3,
    title: 'Qubo Platform',
    description: 'Comprehensive software to visualize, manage and analyze inspections. Access complete histories, generate custom reports and get operational insights.',
    features: [
      'Real-time dashboard',
      'Customizable reports',
      'Predictive analytics',
      'Integration APIs'
    ],
    image: 'https://images.unsplash.com/photo-1526628953301-3e589a6a8b74?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwzfHxzb2Z0d2FyZSUyMHBsYXRmb3JtJTIwZGFzaGJvYXJkJTIwaW50ZXJmYWNlfGVufDB8fHx8MTc3NjE4NDk5NXww&ixlib=rb-4.1.0&q=85',
    badge: 'Software',
    icon: 'monitor'
  },
  {
    id: 4,
    title: 'Integrations',
    description: 'Native connection with ERP systems, CRM, fleet management platforms and third-party tools to automate workflows.',
    features: [
      'RESTful APIs',
      'Real-time webhooks',
      'Pre-built connectors',
      'Complete documentation'
    ],
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwyfHxkYXNoYm9hcmQlMjBhbmFseXRpY3MlMjBpbnRlZ3JhdGlvbiUyMHRlY2hub2xvZ3l8ZW58MHx8fHwxNzc2MTg0Nzg5fDA&ixlib=rb-4.1.0&q=85',
    badge: 'APIs',
    icon: 'plug'
  }
];

export const howItWorksSteps = [
  {
    id: 1,
    number: '01',
    title: 'Vehicle capture',
    description: 'The driver positions the vehicle in the portal. The system automatically starts photographic capture from multiple angles.'
  },
  {
    id: 2,
    number: '02',
    title: 'AI analysis',
    description: 'Computer vision algorithms process the images, detecting damage, components, plates and vehicle features.'
  },
  {
    id: 3,
    number: '03',
    title: 'Data structuring',
    description: 'Information is organized in structured format: damage type, severity, location, affected component and photographic evidence.'
  },
  {
    id: 4,
    number: '04',
    title: 'Operational use',
    description: 'Data is integrated into your systems to generate reports, quotes, repair tracking or fleet analysis.'
  }
];

export const competitiveAdvantages = [
  {
    id: 1,
    title: 'Modular model from day one',
    description: 'Architecture designed to scale quickly with additional units without re-engineering.'
  },
  {
    id: 2,
    title: 'Instant connection system',
    description: 'Plug-and-play installation that enables deployment in days, not months.'
  },
  {
    id: 3,
    title: 'Optimized costs',
    description: 'Efficient economic model that enables scalability without compromising quality.'
  },
  {
    id: 4,
    title: 'Infrastructure + AI + Software',
    description: 'A comprehensive solution combining hardware, artificial intelligence and platform in a single system.'
  },
  {
    id: 5,
    title: 'Robust, technological design',
    description: 'Industrial-grade hardware with premium aesthetics that inspires trust.'
  },
  {
    id: 6,
    title: 'Global scalability',
    description: 'Technology ready to operate across multiple markets and regulations from early stages.'
  }
];

export const businessModel = {
  title: 'Business model',
  subtitle: 'Three integrated monetization layers',
  layers: [
    {
      id: 1,
      title: 'Infrastructure',
      subtitle: 'Revenue from deployment',
      description: 'Our system is sold through direct sales, enabling agile adoption and revenue generation from day one.',
      icon: 'box'
    },
    {
      id: 2,
      title: 'Software',
      subtitle: 'Recurring revenue and operational control',
      description: 'Access to the platform through a monthly subscription model that centralizes reports, vehicle history and real-time analytics, generating ongoing value for every customer.',
      icon: 'monitor'
    },
    {
      id: 3,
      title: 'Data',
      subtitle: 'Scalability and monetization',
      description: 'Every inspection generates a verifiable vehicle history, creating a data layer with value for insurers, fleets and manufacturers.',
      icon: 'database'
    }
  ],
  closingStatement: 'A model that scales with every interaction. Every scan generates revenue and builds a data asset.'
};

export const whyQuboData = {
  title: 'Why Qubo?',
  subtitle: 'The difference between a manual inspection and an operational transformation',
  comparison: {
    before: {
      title: 'Traditional inspection',
      items: [
        'Manual inspection prone to human error',
        'Inconsistent results between inspectors',
        'Slow process that creates operational bottlenecks',
        'Unstructured data that’s hard to analyze',
        'No traceability or verifiable vehicle history',
        'Hidden costs from damage not detected in time',
        'Impossible to scale without adding staff',
        'Frequent disputes with no objective evidence',
        'Information lost during handoffs',
        'No predictive analysis capability'
      ]
    },
    after: {
      title: 'With Qubo',
      items: [
        'Automatic detection with high accuracy',
        'Objective, consistent results every time',
        'Complete inspection in under 60 seconds',
        'Structured data ready for immediate decisions',
        'Complete, auditable history for every vehicle',
        'Immediate ROI from preventing bigger costs',
        'Scalability with no operational limits',
        'Photographic evidence that eliminates disputes',
        'Centralized, always-accessible information',
        'AI that anticipates problems before they happen'
      ]
    }
  }
};

export const scaleData = {
  title: 'Designed to scale',
  subtitle: null,
  description: 'Our systems are designed to grow with your business. From a single unit to multi-portal installations, Qubo’s modular architecture allows expansion without operational disruption.',
  image: 'https://customer-assets.emergentagent.com/job_auto-scan-ai/artifacts/248osucc_33c92793-73f5-4e63-bfca-f5c6aa2480d6.png',
  features: [
    {
      id: 1,
      title: 'Modular installation',
      description: 'Start with one unit and expand according to demand'
    },
    {
      id: 2,
      title: 'Simultaneous operation',
      description: 'Multiple portals working in parallel for higher throughput'
    },
    {
      id: 3,
      title: 'Centralized management',
      description: 'Control all your units from a single platform'
    },
    {
      id: 4,
      title: 'Fast deployment',
      description: 'Installation and go-live in days, not months'
    }
  ]
};

export const faqData = [
  {
    id: 1,
    question: 'How long does a complete inspection take?',
    answer: 'A complete vehicle inspection takes less than 60 seconds. The vehicle simply drives through the portal while our cameras automatically capture images from every angle.'
  },
  {
    id: 2,
    question: 'How accurate is the detection system?',
    answer: 'Our artificial intelligence system has high accuracy in detecting damage and anomalies. In addition, every inspection is backed by high-resolution photographic evidence to ensure objectivity.'
  },
  {
    id: 3,
    question: 'Is specialized staff required to operate the system?',
    answer: 'No. Portal Qubo is designed for autonomous operation. The system automatically starts capture when it detects the vehicle, processes the images and generates reports with no human intervention.'
  },
  {
    id: 4,
    question: 'How does it integrate with our current systems?',
    answer: 'Qubo offers RESTful APIs and webhooks that allow direct integration with ERP systems, CRM, fleet management platforms and other business tools. We also offer pre-built connectors for the most common platforms.'
  },
  {
    id: 5,
    question: 'What happens with inspection data?',
    answer: 'All data is stored securely and in structured form on the Qubo Platform. You have full access to histories, custom reports and predictive analytics. The data is entirely yours and can be exported at any time.'
  },
  {
    id: 6,
    question: 'How much space is needed for installation?',
    answer: 'The standard Portal Qubo requires approximately 4-5 meters in length by 3-4 meters in width. For smaller spaces, we offer the Portal Qubo Compact with a smaller footprint without compromising capture quality.'
  },
  {
    id: 7,
    question: 'Does the system work outdoors?',
    answer: 'Yes, Portal Qubo is designed for 24/7 operation both indoors and outdoors. The hardware is industrial-grade and built to withstand different weather conditions.'
  },
  {
    id: 8,
    question: 'What is the business model?',
    answer: 'Direct purchase of the system plus a monthly subscription. Includes access to the Qubo Platform, updates and technical support.'
  }
];
